import type { ReactNode } from 'react';
import {
  Badge,
  Box,
  Flex,
  Heading,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';

import { PreviewDataStatusBadge } from './PreviewDataStatusBadge';

type EyebrowTone = 'green' | 'orange' | 'primary';

function eyebrowColor(tone: EyebrowTone, mode: 'light' | 'dark'): string {
  const map: Record<EyebrowTone, { light: string; dark: string }> = {
    green: { light: 'green.600', dark: 'green.300' },
    orange: { light: 'orange.600', dark: 'orange.300' },
    primary: { light: 'primary.600', dark: 'primary.300' },
  };
  return map[tone][mode];
}

type PreviewSectionPanelProps = {
  eyebrow: string;
  eyebrowTone?: EyebrowTone;
  title: string;
  description: string;
  children: ReactNode;
  /** Tighter padding for dense tables; default is standard preview padding. */
  compactContent?: boolean;
  /** Prominent pulsing live/sample indicator. */
  dataStatus?: 'live' | 'sample';
  /** Short note under sample badge (keep brief). */
  sampleNote?: string;
  /** Simple header badge when not using dataStatus (e.g. Our mission). */
  headerBadge?: { label: string; colorScheme?: string };
  /** Drop the large title block so dense tables start higher on the page. */
  hideHeader?: boolean;
};

export function PreviewSectionPanel({
  eyebrow,
  eyebrowTone = 'primary',
  title,
  description,
  children,
  compactContent = false,
  dataStatus,
  sampleNote,
  headerBadge,
  hideHeader = false,
}: PreviewSectionPanelProps) {
  const shellBg = useColorModeValue('white', 'gray.800');
  const shellBorder = useColorModeValue('gray.200', 'whiteAlpha.120');
  const headerBg = useColorModeValue('gray.50', 'blackAlpha.300');
  const headerBorder = useColorModeValue('gray.200', 'whiteAlpha.100');
  const headingColor = useColorModeValue('gray.900', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const eyebrowTextColor = useColorModeValue(
    eyebrowColor(eyebrowTone, 'light'),
    eyebrowColor(eyebrowTone, 'dark'),
  );

  return (
    <Box
      borderRadius="16px"
      border="1px solid"
      borderColor={shellBorder}
      bg={shellBg}
      overflow="hidden"
      minW={0}
      maxW="100%"
      boxShadow="0 18px 48px -24px rgba(0, 0, 0, 0.55)"
    >
      {hideHeader ? null : (
        <Box
          px={{ base: 5, md: 8 }}
          py={{ base: 5, md: 6 }}
          bg={headerBg}
          borderBottom="1px solid"
          borderColor={headerBorder}
        >
          <Flex
            direction={{ base: 'column', md: 'row' }}
            align={{ base: 'flex-start', md: 'flex-end' }}
            justify="space-between"
            gap={4}
          >
            <Box flex={1}>
              <Text
                fontSize="xs"
                fontWeight="bold"
                letterSpacing="0.14em"
                textTransform="uppercase"
                color={eyebrowTextColor}
                mb={2}
              >
                {eyebrow}
              </Text>
              <Heading
                as="h3"
                fontSize={{ base: '2xl', md: '4xl' }}
                fontWeight="bold"
                color={headingColor}
                mb={2}
              >
                {title}
              </Heading>
              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                color={subtitleColor}
                maxW="640px"
                lineHeight="1.6"
              >
                {description}
              </Text>
            </Box>
            {dataStatus ? (
              <PreviewDataStatusBadge
                kind={dataStatus}
                note={dataStatus === 'sample' ? sampleNote : undefined}
              />
            ) : headerBadge ? (
              <Badge
                colorScheme={headerBadge.colorScheme ?? 'gray'}
                variant="subtle"
                fontSize="xs"
                flexShrink={0}
              >
                {headerBadge.label}
              </Badge>
            ) : null}
          </Flex>
        </Box>
      )}
      <Box
        px={compactContent ? { base: 3, md: 5 } : { base: 5, md: 8 }}
        py={compactContent ? { base: 3, md: 4 } : { base: 5, md: 7 }}
      >
        {hideHeader && (dataStatus || headerBadge) ? (
          <Flex justify="flex-end" mb={2}>
            {dataStatus ? (
              <PreviewDataStatusBadge
                kind={dataStatus}
                note={dataStatus === 'sample' ? sampleNote : undefined}
              />
            ) : headerBadge ? (
              <Badge
                colorScheme={headerBadge.colorScheme ?? 'gray'}
                variant="subtle"
                fontSize="xs"
                flexShrink={0}
              >
                {headerBadge.label}
              </Badge>
            ) : null}
          </Flex>
        ) : null}
        {children}
      </Box>
    </Box>
  );
}
